/**
 * Mirror of backend src/audit/audit-action.enum.ts.
 * Update both when adding new audit actions.
 */

export const AUDIT_ACTIONS = [
  // Auth
  'LOGIN',
  'LOGIN_FAIL',
  'LOGIN_LOCKED',
  'LOGOUT',
  'AUTO_LOGOUT',
  'PASSWORD_RESET',
  'PASSWORD_CHANGE',
  'PASSWORD_CHANGED',
  'MFA_ENABLE',
  'MFA_DISABLE',
  'MFA_VERIFY',
  'MFA_VERIFY_FAIL',
  'REFRESH_TOKEN',
  // PHI / screenings
  'VIEW_CLIENT',
  'EDIT_CLIENT',
  'VIEW_SCREENING',
  'EDIT_SCREENING',
  'START_SCREENING',
  'SUBMIT_SCREENING',
  'REVOKE_SUBMIT',
  'ASSIGNMENT_ABANDONED',
  // Batches / assignments
  'PREVIEW_BATCH',
  'UPLOAD_BATCH',
  'ASSIGN',
  'REASSIGN',
  'MARK_DNC',
  // Reports
  'EXPORT_REPORT',
  'EXPORT_CSV',
  // Users
  'USER_CREATE',
  'USER_UPDATE',
  'USER_DEACTIVATE',
  'PROFILE_UPDATED',
  // Bookings
  'BOOKING_CREATED',
  'BOOKING_STATUS_CHANGED',
  'BOOKING_RESCHEDULED',
  'BOOKING_NOTES_EDITED',
  'BOOKING_DELETED',
  // System
  'CONFIG_CHANGE',
  'SYSTEM_CONFIG_UPDATED',
  // Bug reports
  'BUG_REPORT_RESOLVED',
  'BUG_REPORT_REOPENED',
  // Uploader
  'SCREENING_UPLOADED_TO_FINDHELP',
  'SCREENING_FLAGGED_BY_UPLOADER',
  'FINDHELP_SLA_BREACH',
  // Household
  'HOUSEHOLD_MEMBER_CAPTURED',
  'HOUSEHOLD_MEMBER_UPDATED',
] as const;

export type AuditAction = typeof AUDIT_ACTIONS[number];

/** Group audit actions for the filter dropdown (UX). */
export const AUDIT_ACTION_GROUPS: Array<{ label: string; actions: AuditAction[] }> = [
  {
    label: 'Auth',
    actions: [
      'LOGIN',
      'LOGIN_FAIL',
      'LOGIN_LOCKED',
      'LOGOUT',
      'AUTO_LOGOUT',
      'PASSWORD_RESET',
      'PASSWORD_CHANGE',
      'PASSWORD_CHANGED',
      'MFA_ENABLE',
      'MFA_DISABLE',
      'MFA_VERIFY',
      'MFA_VERIFY_FAIL',
      'REFRESH_TOKEN',
    ],
  },
  {
    label: 'Bookings',
    actions: ['BOOKING_CREATED', 'BOOKING_STATUS_CHANGED', 'BOOKING_RESCHEDULED', 'BOOKING_NOTES_EDITED', 'BOOKING_DELETED'],
  },
  {
    label: 'Screenings / PHI',
    actions: [
      'VIEW_CLIENT',
      'EDIT_CLIENT',
      'VIEW_SCREENING',
      'EDIT_SCREENING',
      'START_SCREENING',
      'SUBMIT_SCREENING',
      'REVOKE_SUBMIT',
      'ASSIGNMENT_ABANDONED',
    ],
  },
  {
    label: 'Uploader',
    actions: ['SCREENING_UPLOADED_TO_FINDHELP', 'SCREENING_FLAGGED_BY_UPLOADER', 'FINDHELP_SLA_BREACH', 'BUG_REPORT_RESOLVED', 'BUG_REPORT_REOPENED'],
  },
  {
    label: 'Household',
    actions: ['HOUSEHOLD_MEMBER_CAPTURED', 'HOUSEHOLD_MEMBER_UPDATED'],
  },
  {
    label: 'Batches / assignments',
    actions: ['PREVIEW_BATCH', 'UPLOAD_BATCH', 'ASSIGN', 'REASSIGN', 'MARK_DNC'],
  },
  {
    label: 'Reports',
    actions: ['EXPORT_REPORT', 'EXPORT_CSV'],
  },
  {
    label: 'User management',
    actions: ['USER_CREATE', 'USER_UPDATE', 'USER_DEACTIVATE', 'PROFILE_UPDATED'],
  },
  {
    label: 'System',
    actions: ['CONFIG_CHANGE', 'SYSTEM_CONFIG_UPDATED'],
  },
];

/**
 * Coarse category for color-coding the action pill.
 * Matches the Catalog E pastel palette.
 */
export type AuditCategory =
  | 'auth'
  | 'bookings'
  | 'screenings'
  | 'uploader'
  | 'household'
  | 'system'
  | 'phi'
  | 'batch'
  | 'report'
  | 'user'
  | 'unknown';

export function actionCategory(action: string): AuditCategory {
  if (
    action.startsWith('LOGIN') ||
    action.startsWith('LOGOUT') ||
    action.startsWith('AUTO_LOGOUT') ||
    action.startsWith('PASSWORD_') ||
    action.startsWith('MFA_') ||
    action === 'REFRESH_TOKEN'
  ) {
    return 'auth';
  }
  if (action.startsWith('BOOKING_')) return 'bookings';
  if (
    action === 'START_SCREENING' ||
    action === 'SUBMIT_SCREENING' ||
    action === 'VIEW_SCREENING' ||
    action === 'EDIT_SCREENING' ||
    action === 'REVOKE_SUBMIT' ||
    action === 'ASSIGNMENT_ABANDONED' ||
    action === 'VIEW_CLIENT' ||
    action === 'EDIT_CLIENT'
  ) {
    return 'screenings';
  }
  if (
    action === 'SCREENING_UPLOADED_TO_FINDHELP' ||
    action === 'SCREENING_FLAGGED_BY_UPLOADER' ||
    action === 'FINDHELP_SLA_BREACH' ||
    action.startsWith('BUG_REPORT_')
  ) {
    return 'uploader';
  }
  if (action.startsWith('HOUSEHOLD_MEMBER_')) return 'household';
  if (
    action === 'PREVIEW_BATCH' ||
    action === 'UPLOAD_BATCH' ||
    action === 'ASSIGN' ||
    action === 'REASSIGN' ||
    action === 'MARK_DNC'
  ) {
    return 'batch';
  }
  if (action.startsWith('EXPORT_')) return 'report';
  if (action.startsWith('USER_') || action === 'PROFILE_UPDATED') return 'user';
  if (
    action === 'CONFIG_CHANGE' ||
    action === 'SYSTEM_CONFIG_UPDATED' ||
    action === 'INCIDENT_RECOVERY'
  ) {
    return 'system';
  }
  return 'unknown';
}

/** Tailwind tone classes for the category pill (Catalog E pastels). */
export function categoryTone(cat: AuditCategory): string {
  switch (cat) {
    case 'auth':
      return 'bg-sky-50 text-sky-900 border-sky-200';
    case 'bookings':
      return 'bg-peach-50 text-peach-900 border-peach-200';
    case 'screenings':
    case 'phi':
      return 'bg-mint-50 text-mint-900 border-mint-200';
    case 'uploader':
      return 'bg-lavender-50 text-lavender-900 border-lavender-200';
    case 'household':
      return 'bg-rose-50 text-rose-900 border-rose-200';
    case 'system':
      return 'bg-ink-100 text-ink-800 border-ink-200';
    case 'batch':
      return 'bg-lavender-50 text-lavender-900 border-lavender-200';
    case 'report':
      return 'bg-mint-50 text-mint-900 border-mint-200';
    case 'user':
      return 'bg-peach-50 text-peach-900 border-peach-200';
    default:
      return 'bg-ink-100 text-ink-700 border-ink-200';
  }
}

/** Categories for the polished filter dropdown (drives quick category filter). */
export const FILTER_CATEGORIES: Array<{ value: AuditCategory; tk: string }> = [
  { value: 'auth', tk: 'admin.audit.category.auth' },
  { value: 'bookings', tk: 'admin.audit.category.bookings' },
  { value: 'screenings', tk: 'admin.audit.category.screenings' },
  { value: 'uploader', tk: 'admin.audit.category.uploader' },
  { value: 'household', tk: 'admin.audit.category.household' },
  { value: 'system', tk: 'admin.audit.category.system' },
];
